import { type FC, useCallback, useEffect, useRef } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaRegTrashAlt } from "react-icons/fa";
import { MdOutlineEdit } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { deletePostRequest } from "../../../../redux/broadcast/broadcast.actions";
import { setOpenMenu } from "../../../../redux/menus/actions";
import { selectorOpenMenuId } from "../../../../redux/menus/selectors";
import { showModal } from "../../../../redux/modals/actions";
import { setEditingPost } from "../../../../redux/post-editing/actions";
import { selectorEditingPostId } from "../../../../redux/post-editing/selectors";
import { colours } from "../../../../styles/colours.constants";
import { PostType } from "../../../../types/post.types";
import Icon from "../../../icon/icon";
import ToolbarDropdown from "../../../toolbar-dropdown/toolbar-dropdown";
import type { ToolbarOptionConfig } from "../../../toolbar-dropdown/toolbar-dropdown.types";
import { usePostBroadcastContext } from "../../post-broadcast.context";
import * as CSS from "./post-actions.styles";

const POST_ACTION_OPTIONS: ToolbarOptionConfig[] = [
	{ id: "edit", label: "Edit", icon: MdOutlineEdit },
	{ id: "delete", label: "Delete", icon: FaRegTrashAlt },
];

type PostActionsProps = {
	postId: string;
	postType: PostType;
};

const PostActions: FC<PostActionsProps> = ({ postId, postType }) => {
	const dispatch = useDispatch();
	const openMenuId = useSelector(selectorOpenMenuId);
	const editingPostId = useSelector(selectorEditingPostId);
	const containerRef = useRef<HTMLDivElement>(null);
	const isOpen = openMenuId === postId;
	const isEditingThisPost = editingPostId === postId;
	const { broadcastId } = usePostBroadcastContext();

	const options =
		postType === PostType.FAN_COMMENT
			? POST_ACTION_OPTIONS.filter((option) => option.id !== "edit")
			: POST_ACTION_OPTIONS;

	const handleShowPostMenu = useCallback(() => {
		dispatch(setOpenMenu(isOpen ? null : postId));
	}, [dispatch, isOpen, postId]);

	const handleMenuItemSelected = useCallback(
		(id: string) => {
			if (id === "delete") {
				dispatch(
					showModal({
						id: "delete-post",
						onConfirm: () => {
							if (broadcastId) {
								dispatch(
									deletePostRequest({
										broadcastId,
										postId,
									}),
								);
							}
						},
						title: "Delete post",
						description: "Are you sure you want to delete this post?",
					}),
				);
			}

			if (id === "edit") {
				dispatch(setEditingPost(postId));
			}

			dispatch(setOpenMenu(null));
		},
		[dispatch, postId, broadcastId],
	);

	useEffect(() => {
		if (!isOpen) return;

		const handleClickOutside = (event: MouseEvent) => {
			if (!containerRef.current?.contains(event.target as Node)) {
				dispatch(setOpenMenu(null));
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isOpen, dispatch]);

	if (isEditingThisPost) {
		return null;
	}

	return (
		<CSS.PostActions ref={containerRef}>
			<Icon
				color={colours.greyDarker}
				icon={BsThreeDotsVertical}
				size={24}
				as="button"
				onClick={handleShowPostMenu}
			/>
			{isOpen && (
				<ToolbarDropdown
					items={options}
					onMenuItemSelected={handleMenuItemSelected}
					align="right"
				/>
			)}
		</CSS.PostActions>
	);
};

export default PostActions;
